/**
 * advance.ts — a pure, deterministic per-tick simulation step.
 *
 * Invoked by loop.ts at the fixed 10 Hz simulation cadence. The function
 * mutates GameState in place, driven only by the current state (plus
 * RNG draws via state.rng). It must NOT read wall-clock time, Math.random,
 * or any external sources — that's the non-negotiable reproducibility
 * contract from MULTIPLAYER §2.
 *
 * v1 scope of per-tick work:
 *  1. Per-planet population growth (+0.01% per tick up to cap).
 *  2. Per-pop resource generation (0.1 Cr / 0.05 Min / 0.02 Fuel / 0.05 RP).
 *  2b. Planet-side structures pass — upkeep deduction, hull auto-repair,
 *      build/upgrade countdown with level promotion on completion
 *      (STRUCTURES §4). Runs right after economy so upkeep nets against
 *      the income that landed this tick.
 *  2c. Shipyard pass — tick each planet's shipBuildQueue[0]; on completion
 *      spawn a ShipInstance at a ring around the planet and shift the
 *      queue. Runs after structures so a Shipyard that just came online
 *      is visible to the advance pass (though the build gate is enforced
 *      at queue time, not tick time).
 *  3. Terraforming countdown (decrements ticksRemaining, converts biome at 0).
 *  4. EMP disablement countdown on ship instances.
 *  4b. Cloak energy drain — cloaked ships lose 1 energy per tick; drop
 *      cloak automatically when they run out, get EMP-disabled, or enter
 *      hyperspace (MECHANICS §2).
 *  4c. Ship energy regen — uncloaked, in-system, non-disabled ships
 *      recover template.energyRegen per tick, clamped to energyCapacity.
 *      Runs after drain so a just-decloaked ship still tops up the same
 *      tick it lost cloak.
 *  5. Sublight movement — ships with a moveOrder step toward target by
 *     sublightSpeed/tick, de-conflict on arrival so none stack.
 *  6. In-progress per-ship jumps (decrements jumpTicksRemaining, lands on 0).
 *  6b. Pending auto-jumps — ships that arrived at a JP via moveAndJumpShips
 *      fire their hyperdrive now, AFTER the decrement pass so they land at
 *      their full jumpTicksRemaining rather than duration-1.
 *  7. Detection pass — rebuilds per-team VisibilityRecords; runs after
 *     movement/jumps so scanners sit at their final tick positions, and
 *     before combat so firing resolves against the freshly-updated vision
 *     map (MECHANICS §3).
 *  7b. Combat pass — each non-cloaked, non-disabled, in-system ship with
 *      live weapon slots picks a visible target (via pickTarget), rolls
 *      hit, applies damage-type-modified damage through the shield→armor→
 *      hull chain, stamps lastFiredTick, then regens Concussion magazines.
 *      Dead ships (hull ≤ 0) are despawned at the end of the pass
 *      (MECHANICS §1.2 / §3).
 *  7c. Bombardment pass (v2M5) — ships with `bombardOrder` fire unspent
 *      slots at their target planet. Damage scales to Pop kills via
 *      BOMBARDMENT_POP_KILL_FACTOR; per-volley accuracy roll against
 *      planet-implicit Dodge=0. On 10-tick batch boundaries: compute
 *      Loyalty drop via `ceil(2 × kills/popBaseline × 100)`, regen
 *      Loyalty for un-bombarded planets, fire ownership-flip /
 *      depopulation transitions. Runs after combat so cooldowns are
 *      accurate (combat priority on shared slots).
 *  7d. AI pass (M9) — rate-limited per AI_TICK_INTERVAL. Each non-player
 *      empire runs its race-specific behavior tree: one economy action
 *      from the priority list (structure, ship, research, Constructor
 *      build, upgrade) + movement orders for idle ships (patrol near
 *      home, or close with visible enemies for aggressive races).
 *      Runs last so the AI sees the fully-updated state this tick
 *      (economy folded, combat aftermath, etc.).
 *  8. Tick++ at the end.
 *
 * Each step is kept small and branch-free where practical so it shows up
 * cleanly as separate profile frames.
 */

import type {
  Empire,
  GameState,
  Planet,
  ShipInstance,
  ShipInstanceId,
  SystemId,
} from '../state/types';
import { EMPIRE_NEUTRAL_ID } from '../state/types';
import {
  habitabilityFor,
  popDeltaPerTick,
  productionEfficiencyMultiplier,
} from './balance-habitability';
import { advanceDetection } from './advance-detection';
import { advanceCloakEnergyDrain } from './advance-cloak';
import { advanceShipEnergyRegen } from './advance-energy-regen';
import { advanceShipShieldRegen } from './advance-shield-regen';
import { advanceShipCommands } from './advance-ship-commands';
import { advanceCombat } from './advance-combat';
import { advanceMinefield } from './advance-minefield';
import { advanceOdp } from './advance-odp';
import { advanceCarrierSquadrons } from './advance-carrier-squadrons';
import { advanceSurfaceBatteries } from './advance-surface-batteries';
import { advanceBombardment } from './advance-bombardment';
import { advanceMissiles } from './advance-missiles';
import { advanceGameEnd } from './advance-game-end';
import { advanceStructures } from './advance-structures';
import { advanceShipyard } from './advance-shipyard';
import { advanceResearch } from './advance-research';
import { advanceConstruction } from './advance-construction';
import { advanceRepairYard } from './advance-repair-yard';
import { advanceSpaceExtractors } from './advance-extractors';
import { advanceAi } from './advance-ai';
import { hyperdriveCostMultiplier, sublightSpeedMultiplier } from './research-effects';
import { diplomaticCap } from './balance-structures';
import { applyColonise, cancelShipCommandOnJump } from './commands';
import { advanceWarpGateLinks } from './advance-warp-gate-links';
import { auraBonusesForShip } from './auras';
import {
  diplomaticPerTickForEmpire,
  economyMultipliersForPlanet,
  resourceCapsForEmpire,
} from './planet-economy';

/**
 * Minimum distance (in local 0..1 in-system coords) between two idle ships.
 * Sized so two Class-6 ships (the largest, ~36 px tall on screen at the
 * v2M7 silhouette scale) just don't overlap at the camera's MAX_ZOOM=8 —
 * i.e. 36 / (8 × viewW) world units, with viewW conservatively assumed
 * at 900 px and a small safety margin. Anything tighter and Class-6
 * ships visibly clip when fully zoomed in.
 *
 * MFix1.2 dropped this from 0.015 (calibrated for the smaller v1 chevron
 * before per-class size scaling landed in v2M7). At zoom levels below
 * MAX_ZOOM the resulting on-screen gap is wider — that's a deliberate
 * trade per the playtest design lock: "based on preventing ships
 * overlapping when player is max zoomed in." Player zooms in to inspect
 * the precise layout; zoomed-out rendering accepts visual overlap.
 *
 * Stays a hair smaller than formationSpacing (0.02) so a freshly-issued
 * formation order doesn't trip de-conflict on every slot at construction.
 *
 * v3M10.1: migrated to state.balanceData.movement.shipMinSeparation.
 */

/**
 * Global multiplier applied to every template's sublightSpeed at the movement
 * step. Authored sublight speeds in ships.xlsx were originally tuned for feel
 * on a theoretical full-size map; playtest on the letterboxed tactical view
 * showed ships crossing a system in only a few ticks, which makes positioning
 * feel trivial. This single knob slows the whole fleet uniformly so relative
 * class speeds (Scout vs Cruiser) are preserved.
 *
 * v3M10.1: migrated to state.balanceData.movement.sublightSpeedScale.
 */

/** Advance the simulation by exactly one tick. */
export function advanceTick(state: GameState): void {
  // v3M12 — once the game is over, the world freezes. Every advance
  // pass is skipped and the tick counter doesn't increment, so
  // anything reading sim state (the renderer, the end-game overlay)
  // sees a stable, non-evolving final frame.
  if (state.gameOutcome !== 'in-progress') return;
  advancePopulationAndEconomy(state);
  advanceSpaceExtractors(state);
  advanceResearch(state);
  advanceStructures(state);
  advanceShipyard(state);
  advanceConstruction(state);
  // v3M18 — Warp Gate connection lifecycle. Runs after construction so
  // a just-spawned gate is queryable for connect commands this tick;
  // before combat / minefield so a sever-on-destroy via despawnShip
  // still drops the link cleanly even if a gate dies the same tick a
  // link goes live.
  advanceWarpGateLinks(state);
  advanceRepairYard(state);
  advanceTerraforming(state);
  advanceShipStatus(state);
  advanceCloakEnergyDrain(state);
  advanceShipEnergyRegen(state);
  advanceShipShieldRegen(state);
  // v3M22.2 — translate ship commandKind ('patrol' / 'guard') into
  // moveOrder writes BEFORE the movement step so the same tick steps
  // toward the freshly-written target. 'idle' / 'explore' ships pass
  // through untouched ('explore' lands in v3M22.3).
  advanceShipCommands(state);
  advanceShipMovement(state);
  advanceShipJumps(state);
  advancePendingJumps(state);
  advancePendingColonisations(state);
  advanceDetection(state);
  advanceCombat(state);
  // v3M2.2b — ODP ring deployment + level sync. Runs after combat so
  // destroyed ring slots are observed and after structures so level
  // promotions are observed. Idempotent on stable state.
  advanceOdp(state);
  // v3M17.2 — Carrier squadron lifecycle (rebuild ticking, Defense
  // Behaviour auto-recall on liveFighterCount threshold, Attack
  // Behaviour auto-launch). Runs after combat so post-combat fighter
  // counts drive the recall decisions, and before advanceAi so the AI
  // pass observes the freshly launched / recalled state. Squadrons
  // spawned by auto-launch THIS tick will fire from NEXT tick onward
  // (combat already ran for this tick).
  advanceCarrierSquadrons(state);
  // v3M2.3b — Minefield contact damage + depletion. Runs after combat
  // so this tick's combat damage is settled before mine triggers, and
  // before surface batteries / bombardment so a ship damaged by mines
  // can still be cleaned up by the same end-of-tick despawn pass.
  advanceMinefield(state);
  advanceSurfaceBatteries(state);
  // v3M28.3 — Missile impact resolution. Persistent missile entities
  // enqueued by `advance-combat`'s Missile-subType branch age each
  // tick; on arrival (`ageTicks >= ttlTicks`) damage applies through
  // the same shield/armor/hull chain as ship-vs-ship combat and the
  // missile despawns. Runs after combat (newly-launched missiles get
  // one tick of "age 0" before potentially arriving) and after
  // surface batteries (consistent with the position of every other
  // damage-application pass) and before bombardment (which mutates
  // planet state independent of ship combat).
  advanceMissiles(state);
  advanceBombardment(state);
  // v3M12 — runs after bombardment so a planet flip / depopulation
  // resolved earlier this tick can promote into an empire elimination
  // / outcome flip on the same tick. Idempotent past first flip.
  advanceGameEnd(state);
  advanceAi(state);
  state.tick += 1;
}

/**
 * Runs pop growth and resource generation together so we only iterate planets
 * once and keep cache-locality reasonable as the empire grows.
 */
function advancePopulationAndEconomy(state: GameState): void {
  // Per-empire accumulator. Diplomatic is included so Embassy output folds
  // through the same cap-enforcing tail (DIPLOMATIC_CAP from balance-structures).
  interface Delta {
    credits: number;
    research: number;
    minerals: number;
    fuel: number;
    diplomatic: number;
  }
  const deltas = new Map<Empire, Delta>();
  const getDelta = (empire: Empire): Delta => {
    let d = deltas.get(empire);
    if (!d) {
      d = { credits: 0, research: 0, minerals: 0, fuel: 0, diplomatic: 0 };
      deltas.set(empire, d);
    }
    return d;
  };

  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) continue;
    const empire = state.empires.get(planet.ownerEmpireId);
    if (!empire) continue;

    // Per-planet structure multipliers (economy boosters take max-level per
    // kind; see planet-economy.ts). Identity 1.0 when no boosters present.
    const m = economyMultipliersForPlanet(state, planet.id);

    // MFix3.2 — Habitability-driven population dynamics (PLANETS §2.3).
    //   - habitabilityFor produces a per-(planet, race) score in [0, 100].
    //   - popDeltaPerTick returns logistic growth toward popCap when the
    //     band is hospitable / neutral, or linear decline when hostile.
    //   - Cloning Center stacks multiplicatively on positive deltas only.
    //   - productionEfficiency multiplier (band-defined, default 1) applies
    //     to per-Pop resource generation.
    const habitability = habitabilityFor(
      planet,
      empire.raceId,
      empire.researchedTechs,
      state.habitability,
    );
    const baseDelta = popDeltaPerTick(
      habitability,
      planet.currentPopulation,
      planet.populationCap,
      state.habitability,
    );
    const popDelta = baseDelta >= 0 ? baseDelta * m.popGrowth : baseDelta;
    if (popDelta !== 0) {
      planet.currentPopulation = Math.max(
        0,
        Math.min(planet.populationCap, planet.currentPopulation + popDelta),
      );
    }

    // 2) Resource generation from the planet's current pop × per-kind multipliers,
    //    further modulated by Habitability-band production efficiency.
    //    v3M7.1 — non-player empires get a per-tick AI resource bonus from
    //    their difficulty tier (Hard = 1.5×, Easy = 0.7×). Player resources
    //    are unmultiplied; the bonus belongs to the AI side of the game.
    const pop = planet.currentPopulation;
    const eff = productionEfficiencyMultiplier(habitability, state.habitability);
    const aiBonus = empire.isPlayer
      ? 1
      : (state.aiBehaviours.difficulties[empire.aiDifficulty]
          ?.perTickResourceBonus ?? 1);
    const popOutput = state.balanceData.economy.popOutputPerTick;
    const credits = pop * popOutput.credits * m.credits * eff * aiBonus;
    const research = pop * popOutput.research * m.research * eff * aiBonus;
    const minerals = pop * popOutput.minerals * m.minerals * eff * aiBonus;
    const fuel = pop * popOutput.fuel * m.fuel * eff * aiBonus;

    // v3M21.4 — Vassal tribute routing (`DIPLOMACY.md §6.4`). When the
    // planet has an active suzerain, route `vassalTributeFraction` of
    // the per-tick production to the suzerain's delta and credit the
    // remaining fraction to the planet's owner (the synthetic neutral).
    // Pop stays on the planet — only resource flow shifts. Tribute is
    // skipped if the suzerain no longer exists (defensive — empire
    // teardown is rare but the iteration here mustn't crash).
    if (planet.vassalOf !== null) {
      const suzerain = state.empires.get(planet.vassalOf);
      if (suzerain) {
        const tributeFraction = state.balanceData.diplomacy.vassalTributeFraction;
        const remain = 1 - tributeFraction;
        const ds = getDelta(suzerain);
        ds.credits += credits * tributeFraction;
        ds.research += research * tributeFraction;
        ds.minerals += minerals * tributeFraction;
        ds.fuel += fuel * tributeFraction;
        const d = getDelta(empire);
        d.credits += credits * remain;
        d.research += research * remain;
        d.minerals += minerals * remain;
        d.fuel += fuel * remain;
        continue;
      }
      // Suzerain gone — fall through to no-tribute path below.
    }

    const d = getDelta(empire);
    d.credits += credits;
    d.research += research;
    d.minerals += minerals;
    d.fuel += fuel;
  }

  // 3) Per-empire Embassy Diplomatic Influence. Flat per Embassy, capped
  //    at DIPLOMATIC_CAP on the cumulative total after the fold below.
  for (const empire of state.empires.values()) {
    const dip = diplomaticPerTickForEmpire(state, empire.id);
    if (dip > 0) getDelta(empire).diplomatic += dip;
  }

  // 3a) v3M21.2 — route Diplomatic income to focus targets. For each
  //     empire with `diplomaticFocus` set, peel the Diplomatic delta
  //     out and credit it to the focused neutral planet's per-empire
  //     Influence pool (`Planet.influence[empireId]`). Treasury fold
  //     in step 4 then sees a zeroed delta for that empire and skips
  //     the treasury credit. Defensive auto-clear: if the focus
  //     target was annexed since focus was set (or otherwise stopped
  //     being a neutral), clear `diplomaticFocus` and let income fall
  //     through to the treasury fold via the unchanged path. The
  //     synthetic neutral empire (EMPIRE_NEUTRAL_ID) never holds a
  //     focus by design — its Embassy income flows to its own
  //     treasury and pools harmlessly to the cap.
  //
  //     v3M21.4 — Federation lock (`DIPLOMACY.md §6.2`): when the
  //     target planet is federated to a *different* empire, the
  //     spending empire's pool caps at `federationThreshold`. Income
  //     beyond the cap is NOT lost — the unrouted portion stays in
  //     `d.diplomatic` and falls through to the treasury via the
  //     existing fold. This lets a player who's been locked out
  //     keep generating Diplomatic into their treasury rather than
  //     wasting Embassy output.
  const federationThreshold = state.balanceData.diplomacy.thresholds.federation;
  for (const empire of state.empires.values()) {
    if (empire.diplomaticFocus === null) continue;
    const target = state.planets.get(empire.diplomaticFocus);
    if (!target || target.ownerEmpireId !== EMPIRE_NEUTRAL_ID) {
      empire.diplomaticFocus = null;
      continue;
    }
    const d = deltas.get(empire);
    if (!d || d.diplomatic <= 0) continue;
    const prior = target.influence.get(empire.id) ?? 0;
    const cap =
      target.federatedWith !== null && target.federatedWith !== empire.id
        ? federationThreshold
        : Infinity;
    const room = Math.max(0, cap - prior);
    const routed = Math.min(d.diplomatic, room);
    if (routed > 0) {
      target.influence.set(empire.id, prior + routed);
    }
    d.diplomatic -= routed; // unrouted portion (cap excess) falls through to treasury
  }

  // 3b) v3M21.3 — Trade income routing (`DIPLOMACY.md §6.3`). For each
  //     neutral planet with one or more empires in `tradingWith`, push
  //     the per-tick Trade payout (Credits + Minerals from
  //     `state.balanceData.diplomacy.trade`) into each trading empire's
  //     delta. The treasury fold in step 4 caps the resulting Credits /
  //     Minerals at the empire's storage cap exactly like Embassy
  //     income — no separate cap on Trade income. Defensive: if the
  //     planet stopped being a neutral (e.g. via Annex) but
  //     `tradingWith` wasn't cleared by `flipPlanet`, we still skip the
  //     payout here because the income only makes sense from a neutral
  //     trade partner.
  const tradeCredits = state.balanceData.diplomacy.trade.creditsPerTick;
  const tradeMinerals = state.balanceData.diplomacy.trade.mineralsPerTick;
  if (tradeCredits > 0 || tradeMinerals > 0) {
    for (const planet of state.planets.values()) {
      if (planet.tradingWith.length === 0) continue;
      if (planet.ownerEmpireId !== EMPIRE_NEUTRAL_ID) continue;
      for (const empireId of planet.tradingWith) {
        const empire = state.empires.get(empireId);
        if (!empire) continue;
        const d = getDelta(empire);
        d.credits += tradeCredits;
        d.minerals += tradeMinerals;
      }
    }
  }

  // 4) Fold into empire pools, clamping storage resources at their caps.
  //    Research stays uncapped (flows into research project, ECONOMY §4.4).
  for (const [empire, d] of deltas) {
    const caps = resourceCapsForEmpire(state, empire.id);
    empire.resources.credits = Math.min(caps.credits, empire.resources.credits + d.credits);
    empire.resources.minerals = Math.min(
      caps.minerals,
      empire.resources.minerals + d.minerals,
    );
    empire.resources.fuel = Math.min(caps.fuel, empire.resources.fuel + d.fuel);
    empire.resources.research += d.research;
    empire.resources.diplomatic = Math.min(
      diplomaticCap(state.structuresData),
      empire.resources.diplomatic + d.diplomatic,
    );
  }

  // 5) Aggregate planet populations into empire.resources.population so the
  //    HUD (and any empire-scope consumer — AI, save files, future
  //    diplomacy) has a single up-to-date total. Population is derived
  //    rather than accumulated per tick: each planet tracks its own
  //    currentPopulation as the authoritative value; this step just sums
  //    them for empire-wide display. Runs every tick so the HUD income-
  //    per-second tracker sees correct deltas on the empire field.
  for (const empire of state.empires.values()) {
    let total = 0;
    for (const pid of empire.planetIds) {
      const planet = state.planets.get(pid);
      if (planet) total += planet.currentPopulation;
    }
    empire.resources.population = total;
  }
}

function advanceTerraforming(state: GameState): void {
  for (const planet of state.planets.values()) {
    if (!planet.terraforming) continue;
    planet.terraforming.ticksRemaining -= 1;
    if (planet.terraforming.ticksRemaining <= 0) {
      applyTerraformComplete(planet);
    }
  }
}

function applyTerraformComplete(planet: Planet): void {
  if (!planet.terraforming) return;
  planet.biome = planet.terraforming.targetBiome;
  planet.terraforming = null;
}

function advanceShipStatus(state: GameState): void {
  for (const ship of state.ships.values()) {
    if (ship.disabledTicks > 0) ship.disabledTicks -= 1;
  }
}

/**
 * Step each ship with an outstanding sublight move order toward its target by
 * its template sublightSpeed per tick. On arrival (one step or less away) snap
 * to the target — de-conflicting onto the nearest free spot if another idle
 * ship is already sitting there — and clear the order.
 *
 * Iteration is done in ascending ship-id order so two ships ordered to the
 * same spot in the same tick resolve their de-conflict in a deterministic
 * sequence. That's what makes the post-arrival layout reproducible across
 * hosts (MULTIPLAYER 2).
 *
 * Disabled (EMP) and mid-jump ships don't move even if they carry an order —
 * the order is retained and re-evaluated on a later tick when the ship recovers.
 */
function advanceShipMovement(state: GameState): void {
  const ordered = [...state.ships.values()].sort((a, b) => a.id - b.id);
  for (const ship of ordered) {
    if (ship.moveOrder === null) continue;
    if (ship.systemId === null) continue; // mid-jump; wait
    if (ship.disabledTicks > 0) continue;

    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(empire.raceId + ':' + ship.chassis);
    if (!tmpl) continue;
    // MFix7.2 — Sublight Speed Mk research multiplies on top of the
    // global sublightSpeedScale and the per-chassis template value.
    // MFix8.5 — friendly aura speedMult (e.g. future "Magician"-style
    // emitter buff) compounds on top. With the v3M14c authoring values
    // at speedMult=1.0, this is a no-op fast path.
    const speedMult = sublightSpeedMultiplier(state, empire);
    const auraSpeedMult = auraBonusesForShip(state, ship).speedMult;
    const speed =
      tmpl.sublightSpeed *
      state.balanceData.movement.sublightSpeedScale *
      speedMult *
      auraSpeedMult;
    if (!(speed > 0)) {
      // Pathological data — clear the order to avoid an infinite loop.
      ship.moveOrder = null;
      continue;
    }

    const dx = ship.moveOrder.targetX - ship.sx;
    const dy = ship.moveOrder.targetY - ship.sy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist <= speed) {
      // Arrive this tick. De-conflict onto a free spot if another idle ship
      // happens to be sitting exactly at the requested target.
      const placed = findFreeSublightSpot(
        state,
        ship.systemId,
        ship.moveOrder.targetX,
        ship.moveOrder.targetY,
        ship.id,
      );
      ship.sx = placed.sx;
      ship.sy = placed.sy;
      ship.moveOrder = null;
      // Note: if ship has a pendingJumpPointId from moveAndJumpShips, the
      // actual hyperdrive fire happens in advancePendingJumps — run after
      // advanceShipJumps so jumpTicksRemaining isn't decremented the same
      // tick the jump starts.
    } else {
      ship.sx += (dx / dist) * speed;
      ship.sy += (dy / dist) * speed;
    }
  }
}

/**
 * Fire the hyperdrive on a ship that has arrived at its pending jump point.
 * Mirrors applyJumpShips' validation + mutation for a single ship:
 *   1. JP exists and is linked.
 *   2. Ship's template defines hyperdriveFuelUse + jumpDurationTicks.
 *   3. Empire has enough fuel for the Mk-1 cost.
 * On success mutates the ship into the mid-jump shape (systemId=null, jump
 * target/duration populated) and deducts fuel from the empire. Always clears
 * pendingJumpPointId on exit — retrying on a later tick would require another
 * arrival check and could surprise the player with a delayed jump.
 */
function tryStartPendingJump(state: GameState, ship: ShipInstance): void {
  const jpId = ship.pendingJumpPointId;
  ship.pendingJumpPointId = null; // clear either way — see fn doc.
  if (jpId === null) return;
  if (ship.jumpTargetSystemId !== null) return; // already mid-jump (defensive)
  const jp = state.jumpPoints.get(jpId);
  if (!jp) return;
  if (jp.linkedSystemId === null) return;
  if (ship.systemId !== jp.systemId) return; // not where the JP is (defensive)

  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return;
  const tmpl = state.shipTemplates.get(empire.raceId + ':' + ship.chassis);
  if (!tmpl) return;

  const cost = tmpl.hyperdriveFuelUse * hyperdriveCostMultiplier(state, empire);
  if (empire.resources.fuel < cost) return;

  // v3M18 — auto-mesh removed. JP-based jumps use the standard
  // per-template `jumpDurationTicks` unconditionally. Player-authored
  // Warp Gate connections are routed through the dedicated
  // jumpShipsViaWarpGate path (v3M18.4) and never modify this code.
  const duration = tmpl.jumpDurationTicks;

  empire.resources.fuel -= cost;
  ship.jumpOriginSystemId = ship.systemId;
  ship.jumpTargetSystemId = jp.linkedSystemId;
  ship.jumpTargetJumpPointId = jp.linkedJumpPointId;
  ship.jumpTargetGateId = null; // v3M18 — JP-jump never lands at a gate
  ship.jumpTicksRemaining = duration;
  ship.jumpDurationTotal = duration;
  ship.systemId = null;
  // moveOrder was already cleared by the caller; no need to re-null.
  // v3M22.5 — cancel any v3M22 high-level command on jump (preserves
  // 'explore' so the explorer's self-issued chain of jumps doesn't
  // self-cancel after the first hop).
  cancelShipCommandOnJump(ship);
}

/**
 * Find a free spot near (desiredX, desiredY) in systemId. A spot is "free"
 * if no other *idle* ship (moveOrder === null, systemId === systemId) in the
 * same system is within SHIP_MIN_SEPARATION. Moving ships don't block — they
 * will step out of the way on their own.
 *
 * Searches in a Vogel (golden-angle) spiral outward from the desired point so
 * the nearest free slot is returned. Falls back to the desired point on the
 * extremely unlikely chance that 50 spiral candidates are all blocked.
 */
function findFreeSublightSpot(
  state: GameState,
  systemId: SystemId,
  desiredX: number,
  desiredY: number,
  excludeShipId: ShipInstanceId,
): { sx: number; sy: number } {
  const minSep = state.balanceData.movement.shipMinSeparation;
  const MIN_SEP_SQ = minSep * minSep;
  const GOLDEN = Math.PI * (3 - Math.sqrt(5));

  const occupied = (x: number, y: number): boolean => {
    for (const other of state.ships.values()) {
      if (other.id === excludeShipId) continue;
      if (other.systemId !== systemId) continue;
      if (other.moveOrder !== null) continue; // only idle ships block
      const odx = other.sx - x;
      const ody = other.sy - y;
      if (odx * odx + ody * ody < MIN_SEP_SQ) return true;
    }
    return false;
  };

  if (!occupied(desiredX, desiredY)) return { sx: desiredX, sy: desiredY };

  for (let i = 1; i < 50; i++) {
    const angle = i * GOLDEN;
    const r = minSep * 1.1 * Math.sqrt(i);
    const cx = desiredX + Math.cos(angle) * r;
    const cy = desiredY + Math.sin(angle) * r;
    if (!occupied(cx, cy)) return { sx: cx, sy: cy };
  }
  return { sx: desiredX, sy: desiredY };
}

function advanceShipJumps(state: GameState): void {
  // Each ship jumps independently so a fast Scout can arrive before the slow
  // Cruiser it left with. Ships with no jumpTargetSystemId are stationary.
  for (const ship of state.ships.values()) {
    if (ship.jumpTargetSystemId === null) continue;
    ship.jumpTicksRemaining -= 1;
    if (ship.jumpTicksRemaining <= 0) {
      const arrivalSystemId = ship.jumpTargetSystemId;
      // v3M18 — warp-link arrival takes precedence over JP arrival.
      // If `jumpTargetGateId` is set and the gate still exists, land
      // the ship next to the partner gate. If the gate was destroyed
      // mid-jump (sever-on-destroy already cleared the link), fall
      // through to JP-arrival (typically null too) and ultimately
      // leave sx/sy as-is — the ship arrives in the partner system at
      // its previous (sx, sy), which is harmless if degenerate.
      if (ship.jumpTargetGateId !== null) {
        const gate = state.ships.get(ship.jumpTargetGateId);
        if (gate && gate.systemId === arrivalSystemId) {
          const placed = findFreeSublightSpot(
            state,
            arrivalSystemId,
            gate.sx,
            gate.sy,
            ship.id,
          );
          ship.sx = placed.sx;
          ship.sy = placed.sy;
        }
      } else if (ship.jumpTargetJumpPointId !== null) {
        // If we captured a destination JP at departure, land the ship at its
        // position (de-conflicted if another idle ship is already there). If
        // not (e.g. tests that don't pair JPs), leave sx/sy as-is — the ship
        // was presumably placed sensibly by the test harness.
        const jp = state.jumpPoints.get(ship.jumpTargetJumpPointId);
        if (jp) {
          const placed = findFreeSublightSpot(
            state,
            arrivalSystemId,
            jp.x,
            jp.y,
            ship.id,
          );
          ship.sx = placed.sx;
          ship.sy = placed.sy;
        }
      }
      ship.systemId = arrivalSystemId;
      ship.jumpOriginSystemId = null;
      ship.jumpTargetSystemId = null;
      ship.jumpTargetJumpPointId = null;
      ship.jumpTargetGateId = null;
      ship.jumpTicksRemaining = 0;
      ship.jumpDurationTotal = 0;
      // MFix6.3 — Mark the destination as scouted for the owning empire.
      // Persistent reveal: once visited, the system stays scouted in
      // galaxy-view fog-of-war forever. Idempotent on Set.add.
      const owner = state.empires.get(ship.ownerEmpireId);
      if (owner) owner.scoutedSystemIds.add(arrivalSystemId);
    }
  }
}

/**
 * Fire the hyperdrive on any ship that arrived at its pending JP this tick.
 * Runs AFTER advanceShipJumps so ships starting a jump here land at their
 * full jumpTicksRemaining rather than duration-1 (the jumps pass would
 * otherwise decrement it on the same tick).
 *
 * Iteration order is ascending ship-id for determinism.
 */
function advancePendingJumps(state: GameState): void {
  const ordered = [...state.ships.values()].sort((a, b) => a.id - b.id);
  for (const ship of ordered) {
    if (ship.systemId === null) continue;
    if (ship.moveOrder !== null) continue;
    if (ship.pendingJumpPointId !== null) {
      tryStartPendingJump(state, ship);
      continue;
    }
    if (ship.pendingWarpGateLinkId !== null) {
      tryStartPendingWarpGateJump(state, ship);
    }
  }
}

/**
 * v3M18.5 — fire the warp-link hyperdrive on a ship that has arrived
 * at its source gate's (sx, sy). Mirrors `tryStartPendingJump` but
 * routes through `state.warpGateLinks` instead of `state.jumpPoints`.
 *
 * Validation chain (silent no-op on failure, with `pendingWarpGateLinkId`
 * cleared either way to avoid re-firing on subsequent ticks):
 *   1. Link still exists, is `'live'`, and is owned by the ship's empire.
 *   2. Ship's current system is one of the link's two endpoints.
 *   3. Empire can afford the per-jump fuel cost.
 *
 * On success: deduct fuel, set jumpOriginSystemId / jumpTargetSystemId
 * / jumpTargetGateId / jumpTicksRemaining (distance-scaled per the
 * v3M18 lock), clear systemId.
 */
function tryStartPendingWarpGateJump(
  state: GameState,
  ship: ShipInstance,
): void {
  const linkId = ship.pendingWarpGateLinkId;
  ship.pendingWarpGateLinkId = null; // clear either way
  if (linkId === null) return;
  if (ship.jumpTargetSystemId !== null) return; // already mid-jump (defensive)
  const link = state.warpGateLinks.get(linkId);
  if (!link) return;
  if (link.status !== 'live') return;
  if (link.ownerEmpireId !== ship.ownerEmpireId) return;
  const sourceSystemId = ship.systemId;
  if (sourceSystemId === null) return;
  if (
    sourceSystemId !== link.systemAId &&
    sourceSystemId !== link.systemBId
  ) {
    return;
  }
  const targetSystemId =
    sourceSystemId === link.systemAId ? link.systemBId : link.systemAId;
  const targetGateId =
    sourceSystemId === link.systemAId ? link.gateBId : link.gateAId;
  const sysA = state.systems.get(link.systemAId);
  const sysB = state.systems.get(link.systemBId);
  if (!sysA || !sysB) return;

  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
  if (!tmpl) return;
  if (tmpl.sublightSpeed <= 0) return; // stationary platform — never jumps

  const cost =
    tmpl.hyperdriveFuelUse * hyperdriveCostMultiplier(state, empire);
  if (empire.resources.fuel < cost) return;

  const galaxyDistance = Math.hypot(sysA.x - sysB.x, sysA.y - sysB.y);
  const duration = Math.max(
    50,
    Math.round(galaxyDistance * tmpl.jumpDurationTicks),
  );

  empire.resources.fuel -= cost;
  ship.jumpOriginSystemId = sourceSystemId;
  ship.jumpTargetSystemId = targetSystemId;
  ship.jumpTargetJumpPointId = null;
  ship.jumpTargetGateId = targetGateId;
  ship.jumpTicksRemaining = duration;
  ship.jumpDurationTotal = duration;
  ship.systemId = null;
  // moveOrder was already cleared by the caller (the arrival predicate).
  // v3M22.5 — cancel any v3M22 high-level command on jump (preserves
  // 'explore' so the explorer's self-issued chain of jumps doesn't
  // self-cancel after the first hop).
  cancelShipCommandOnJump(ship);
}

/**
 * MFix3.4 — fire `applyColonise` on any ship that has arrived at its
 * `pendingColoniseTargetId`. Mirrors `advancePendingJumps`: detects
 * arrival via `moveOrder === null`, then defers to the colonise applier
 * for the full validation chain. Pending field is cleared on attempt
 * (success or silent rejection) so a stale order doesn't retry forever.
 *
 * Iteration order is ascending ship-id for determinism.
 */
function advancePendingColonisations(state: GameState): void {
  const ordered = [...state.ships.values()].sort((a, b) => a.id - b.id);
  for (const ship of ordered) {
    if (ship.pendingColoniseTargetId === null) continue;
    if (ship.moveOrder !== null) continue;
    if (ship.systemId === null) continue;
    const planetId = ship.pendingColoniseTargetId;
    ship.pendingColoniseTargetId = null;
    applyColonise(state, ship.ownerEmpireId, ship.id, planetId);
  }
}
